import React, { FC } from 'react';
import {
  Form as AntdForm,
  Input,
  Divider,
  Row,
  Col,
  DatePicker,
  Select,
  Tabs,
} from 'antd';
import * as texts from 'assets/texts/newsPostConfiguration';
import { FormInstance } from 'antd/es/form';
import {
  DEFAULT_LANGUAGE,
  Language,
  LANGUAGES,
  LocalizedNewsPost,
} from 'apollo/localizedTypes';
import languageLabels from 'utils/i18n/languageLabels';
import toFormLocalizedText from 'utils/i18n/toFormLocalizedText';
import { Button, UploadImages } from 'components/UI';
import { CreateNewsPostFormValues } from 'views/Configuration/NewsPostConfiguration/types';
import orderNewsPostContent from 'views/Configuration/NewsPostConfiguration/utils/orderNewsPostContent';
import moment from 'moment';
import styles from './Form.module.scss';
import useFormViewModel from './form.viewModel';
import { LocalizedRichTextEditor } from './LocalizedRichTextEditor';

export interface FormProps {
  initialValues?: LocalizedNewsPost;
  onFormFinished: (values: CreateNewsPostFormValues, formInstance: FormInstance) => void;
  onCloseModal: () => void;
}

export const Form: FC<FormProps> = ({
  initialValues,
  onFormFinished,
  onCloseModal,
}: FormProps) => {
  const {
    mapCurrentRecommendations,
    uploadImageLoading,
    onCoverImageUpload,
    deleteCoverImage,
    refetchCoverImage,
    coverImage,
    onMainImageUpload,
    deleteMainImage,
    refetchMainImage,
    closingImages,
    onClosingImageUpload,
    deleteClosingImage,
    refetchClosingImage,
    maxClosingImages,
    recommendationIds,
    onChangeRecommendationIds,
    recommendationOptions,
    mainImage,
    maxRecommendationsSelected,
    activeLanguage,
    setActiveLanguage,
    isDefaultLanguage,
    form,
    onCloseModalButtonClicked,
  } = useFormViewModel(initialValues);
  const { Item } = AntdForm;
  const { TabPane } = Tabs;

  const initValues = {
    ...initialValues,
    title: toFormLocalizedText(initialValues?.title),
    publicationDate: initialValues?.publicationDate
      ? moment(initialValues?.publicationDate)
      : null,
    recommendationIds: mapCurrentRecommendations(initialValues),
    content: orderNewsPostContent(initialValues).map((item) =>
      toFormLocalizedText(item.content)
    ),
    closingImagesIds: initialValues?.closingImages?.map((image) => image.id) || [],
    authorId: initialValues?.author,
  };

  const mappedRecommendationOptions = recommendationOptions?.map((item) => ({
    ...item,
    disabled:
      recommendationIds.length >= maxRecommendationsSelected
        ? !recommendationIds.includes(String(item.value))
        : false,
  }));

  const rules = [{ required: true, message: texts.required }];

  const renderLanguageTabs = () => {
    return (
      <Tabs
        activeKey={activeLanguage}
        onChange={(key) => setActiveLanguage(key as Language)}
      >
        {LANGUAGES.map((language) => (
          <TabPane tab={languageLabels[language]} key={language} />
        ))}
      </Tabs>
    );
  };

  const renderTranslatedSection = () => {
    return (
      <>
        <div className={styles.SectionTitle}>
          <b>{texts.translatedContent}</b>
        </div>
        {LANGUAGES.map((key) => {
          const languageRules = key === DEFAULT_LANGUAGE ? rules : undefined;

          return (
            <div key={key} className={key === activeLanguage ? undefined : styles.Hidden}>
              <Item name={['title', key]} label={texts.title} rules={languageRules}>
                <Input />
              </Item>
              <Item
                name={['content', '0', key]}
                label={texts.firstContent}
                rules={languageRules}
              >
                <LocalizedRichTextEditor />
              </Item>
              <Item
                name={['content', '1', key]}
                label={texts.secondContent}
                rules={languageRules}
              >
                <LocalizedRichTextEditor />
              </Item>
            </div>
          );
        })}
      </>
    );
  };

  const renderGeneralSection = () => {
    return (
      <>
        <div className={styles.SectionTitle}>
          <b>{texts.generalContent}</b>
        </div>
        {!isDefaultLanguage && (
          <div className={styles.SectionWarning}>{texts.generalContentWarning}</div>
        )}

        <Row gutter={[24, 0]}>
          <Col span={8}>
            <Item name="publicationDate" label={texts.publicationDate} rules={rules}>
              <DatePicker disabled={!isDefaultLanguage} />
            </Item>
            <Item name={['coverImage', 'id']} label={texts.coverImage} rules={rules}>
              <UploadImages
                images={coverImage}
                parentEntityId={BigInt(initialValues?.id || 0)}
                uploadedImageId={coverImage.length ? BigInt(coverImage[0].id) : undefined}
                uploadUrl={coverImage.length ? coverImage[0].uploadUrl || '' : undefined}
                loading={uploadImageLoading}
                disabled={!isDefaultLanguage}
                isMain
                uploadCallback={onCoverImageUpload}
                deleteCallback={deleteCoverImage}
                refetchImagesCallback={refetchCoverImage}
                refetchOnDelete={false}
              />
            </Item>
          </Col>
          <Col span={8}>
            <Item
              className={styles.TightField}
              name={['mainImage', 'id']}
              label={texts.mainImage}
              rules={rules}
            >
              <UploadImages
                images={mainImage}
                parentEntityId={BigInt(initialValues?.id || 0)}
                uploadedImageId={mainImage.length ? BigInt(mainImage[0].id) : undefined}
                uploadUrl={mainImage.length ? mainImage[0].uploadUrl || '' : undefined}
                loading={uploadImageLoading}
                disabled={!isDefaultLanguage}
                isMain
                uploadCallback={onMainImageUpload}
                deleteCallback={deleteMainImage}
                refetchImagesCallback={refetchMainImage}
                refetchOnDelete={false}
              />
            </Item>
            <Item name={['mainImage', 'caption']} label={texts.caption}>
              <Input disabled={!isDefaultLanguage} />
            </Item>
          </Col>
          <Col span={8}>
            <Item name="closingImagesIds" label={texts.closingImages}>
              <UploadImages
                images={closingImages}
                parentEntityId={BigInt(initialValues?.id || 0)}
                uploadedImageId={
                  closingImages.length
                    ? BigInt(closingImages[closingImages.length - 1].id)
                    : undefined
                }
                uploadUrl={
                  closingImages.length
                    ? closingImages[closingImages.length - 1].uploadUrl || ''
                    : undefined
                }
                loading={uploadImageLoading}
                disabled={!isDefaultLanguage}
                uploadCallback={onClosingImageUpload}
                deleteCallback={deleteClosingImage}
                refetchImagesCallback={refetchClosingImage}
                maxNumberOfImages={maxClosingImages}
                refetchOnDelete={false}
              />
            </Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col span={8}>
            <Item name="author" label={texts.author} rules={rules}>
              <Input disabled={!isDefaultLanguage} />
            </Item>
          </Col>
          <Col span={8}>
            <Item name="recommendationIds" label={texts.recommendations}>
              <Select
                mode="multiple"
                options={mappedRecommendationOptions}
                onChange={onChangeRecommendationIds}
                disabled={!isDefaultLanguage}
              />
            </Item>
          </Col>
        </Row>
      </>
    );
  };

  const renderModalFooterButtons = () => {
    return (
      <Row justify="end">
        <Col>
          <Row justify="space-between" gutter={[4, 0]}>
            <Col>
              <Button
                type="default"
                text={texts.cancel}
                onClick={() => onCloseModalButtonClicked(onCloseModal)}
              />
            </Col>
            <Col>
              {!initialValues ? (
                <Button type="default" text={texts.create} htmlType="submit" />
              ) : (
                <Button type="default" text={texts.save} htmlType="submit" />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <>
      {renderLanguageTabs()}
      <AntdForm
        form={form}
        layout="vertical"
        initialValues={initValues}
        onFinish={(values: CreateNewsPostFormValues) => onFormFinished(values, form)}
      >
        {renderTranslatedSection()}
        <Divider />
        {renderGeneralSection()}
        {renderModalFooterButtons()}
      </AntdForm>
    </>
  );
};

Form.defaultProps = {
  initialValues: undefined,
};
